import React from 'react';
import styles from './Container.module.css';

/**
 * Container
 * A responsive, web-first wrapper with mobile-first breakpoints.
 * Defaults to flex column, full width with centered content and max-widths per breakpoint.
 *
 * Props:
 * - as: string | React.ElementType (default: 'div')
 * - fluid: boolean (no max-width; always 100%)
 * - center: boolean (align-items: center)
 * - fullHeight: boolean (min-height: 100vh)
 * - direction: 'row' | 'column' (default: 'column')
 * - wrap: 'wrap' | 'nowrap' (default: 'nowrap')
 * - gap: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 * - paddingY: 'sm' | 'md' | 'lg' | 'xl'
 * - className: string (additional classes)
 * - style: React.CSSProperties (inline styles)
 */
export function Container({
  as: Component = 'div',
  fluid = false,
  center = false,
  fullHeight = false,
  direction = 'column',
  wrap = 'nowrap',
  gap,
  paddingY,
  className,
  style,
  children,
  ...rest
}) {
  const classes = [styles.container];

  if (fluid) classes.push(styles.fluid);
  if (center) classes.push(styles.center);
  if (fullHeight) classes.push(styles.fullHeight);
  if (direction === 'row') classes.push(styles.row);
  if (direction === 'column') classes.push(styles.column);
  if (wrap === 'wrap') classes.push(styles.wrap);
  if (wrap === 'nowrap') classes.push(styles.noWrap);

  if (gap) {
    const map = { xs: styles.gapXs, sm: styles.gapSm, md: styles.gapMd, lg: styles.gapLg, xl: styles.gapXl };
    if (map[gap]) classes.push(map[gap]);
  }
  if (paddingY) {
    const map = { sm: styles.padYSm, md: styles.padYMd, lg: styles.padYLg, xl: styles.padYXl };
    if (map[paddingY]) classes.push(map[paddingY]);
  }
  if (className) classes.push(className);

  return (
    <Component className={classes.join(' ')} style={style} {...rest}>
      {children}
    </Component>
  );
}
